<template>
  <v-card flat tile height="100%">
    <v-toolbar flat dense height="64">
      <v-toolbar-title class="subtitle-1 font-weight-medium text--secondary">Centro de incorporación</v-toolbar-title>
      <v-spacer />
      <v-btn text small :ripple="false" @click="$emit('close')">
        Ocultar<v-icon size="20px">mdi-chevron-right</v-icon>
      </v-btn>
    </v-toolbar>

    <div class="overflow-y-auto pa-4 pt-0" style="height: calc(100vh - 113px)">
      <!-- Loader -->
      <template v-if="loaderRetrieve">
        <v-skeleton-loader class="py-3" transition="fade-transition" type="card-heading" />
        <v-skeleton-loader class="py-3" transition="fade-transition" type="card-heading" />
      </template>

      <template v-else>
        <!-- banner onboarding incompleto -->
        <v-alert v-if="!completed" class="mb-4" color="primary" dark icon="mdi-alert-box" prominent>
          <span class="body-2">Personaliza tu experiencia de inicio en unos pocos pasos</span>
          <div class="mt-2">
            <v-btn small color="indigo darken-4" :ripple="false" @click="navigate('OrganizationOnboarding')">Vamos<v-icon right small>mdi-arrow-right</v-icon></v-btn>
          </div>
        </v-alert>

        <template v-else>
          <!-- checklist items -->
          <ActivationChecklistItem v-for="(item, i) in checklistItems" @action="navigate(item.route)" :completed="item.completed" :description="item.description" :key="i" :step="i + 1" :title="item.title" />
          <!-- end checklist items -->

          <!-- test data -->
          <v-alert v-if="$store.state.ui.testMode" color="primary" dark icon="mdi-test-tube" prominent>
            <span class="body-2">Hemos cargado datos de prueba para que puedas explorar Piriod sin afectar tu cuenta real</span>
            <div class="mt-2">
              <v-btn x-small color="indigo darken-4" class="mr-2" :ripple="false" @click="navigate('CustomerListCreate')">Ver clientes</v-btn>
              <v-btn v-if="path !== 'invoicing_only'" x-small color="indigo darken-4" class="mr-2" :ripple="false" @click="navigate('SubscriptionList')">Ver suscripciones</v-btn>
              <v-btn v-if="path === 'invoicing_only'" x-small color="indigo darken-4" class="mr-2" :ripple="false" @click="navigate('InvoiceList')">Ver facturas</v-btn>
            </div>
          </v-alert>

          <v-alert v-else color="primary" dark icon="mdi-test-tube-off" prominent>
            <span class="body-2">¿Quieres explorar con datos de ejemplo?</span>
            <div class="mt-2">
              <v-btn x-small color="indigo darken-4" :ripple="false" @click="$store.commit('ui/SET_TEST_MODE', true)">Activar modo prueba</v-btn>
            </div>
          </v-alert>
          <!-- end test data -->
        </template>

        <!-- Recursos -->
        <v-sheet class="mt-3 pa-4" rounded outlined>
          <span class="d-block body-2 font-weight-medium text--secondary mb-2">¿Necesitas ayuda para comenzar?</span>
          <div>
            <v-btn class="mb-6" small color="primary" outlined :ripple="false" @click="intercomNewMessage('Hola, necesito ayuda para activar mi cuenta.')">
              <v-icon left small>mdi-message-text-outline</v-icon>Solicitar ayuda
            </v-btn>
            <a class="d-block body-2 text-decoration-none mb-2" href="https://docs.piriod.com" target="_blank">
              <v-icon small color="primary" class="mr-1">mdi-book-open-variant</v-icon>Ver guía de implementación
            </a>
            <a class="d-block body-2 text-decoration-none mb-2" href="https://docs.piriod.com/api" target="_blank">
              <v-icon small color="primary" class="mr-1">mdi-code-tags</v-icon>Ver recursos para developers
            </a>
            <a class="d-block body-2 text-decoration-none" href="https://www.youtube.com/@piriod" target="_blank">
              <v-icon small color="primary" class="mr-1">mdi-youtube</v-icon>Ir al canal de YouTube
            </a>
          </div>
        </v-sheet>
      </template>
    </div>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import GenericViewMixin from '@/mixins/GenericViewMixin'
import ActivationChecklistItem from '@/components/onboarding/ActivationChecklistItem'
import { onboardingPaths } from '@/collections/onboarding'

export default {
  name: 'QuickstartDrawer',
  mixins: [
    GenericViewMixin
  ],
  components: {
    ActivationChecklistItem
  },
  data: () => ({
    loaderRetrieve: false
  }),
  computed: {
    ...mapState({
      billerconnectionsList: state => state.billers.billerconnectionsList,
      providersList: state => state.payments.providersList,
      path: state => state.onboarding.path,
      completed: state => state.onboarding.completed
    }),
    hasPaymentProvider () {
      return this.providersList.length > 0
    },
    hasBillerConnection () {
      return this.billerconnectionsList.length > 0
    },
    checklistItems () {
      const items = []

      if (this.path === onboardingPaths.SUBSCRIPTION_CARD) {
        items.push({
          title: 'Conecta tu procesador de pagos',
          description: 'Habilita los cobros con tarjeta conectando un procesador de pagos.',
          completed: this.hasPaymentProvider,
          route: 'Integrations'
        })
        items.push({
          title: 'Activa la facturación local',
          description: 'Conecta tu proveedor para emitir facturas electrónicas locales.',
          completed: this.hasBillerConnection,
          route: 'Integrations'
        })
      }

      if (this.path === onboardingPaths.SUBSCRIPTION_TRANSFER) {
        items.push({
          title: 'Activa la facturación local',
          description: 'Conecta tu proveedor para emitir facturas electrónicas locales.',
          completed: this.hasBillerConnection,
          route: 'Integrations'
        })
        items.push({
          title: 'Conecta tu cuenta bancaria',
          description: 'Conecta tu banco para conciliar transferencias automáticamente.',
          completed: false,
          route: 'AccountSettingsBank'
        })
      }

      if (this.path === onboardingPaths.INVOICING_ONLY) {
        items.push({
          title: 'Activa la facturación local',
          description: 'Conecta tu proveedor para emitir facturas electrónicas reales.',
          completed: this.hasBillerConnection,
          route: 'Integrations'
        })
      }

      if (!this.path) {
        items.push({
          title: 'Conecta tu procesador de pagos',
          description: 'Conecta un procesador para cobrar con tarjeta.',
          completed: this.hasPaymentProvider,
          route: 'Integrations'
        })
        items.push({
          title: 'Activa la facturación local',
          description: 'Conecta tu proveedor de facturación electrónica local.',
          completed: this.hasBillerConnection,
          route: 'Integrations'
        })
      }

      return items
    }
  },
  created () {
    this.loaderRetrieve = true

    const loadState = this.$store.state.onboarding.stateLoaded
      ? Promise.resolve()
      : this.$store.dispatch('onboarding/LOAD_STATE')

    loadState.then(() => {
      return Promise.all([
        this.$store.dispatch('payments/LIST', {
          resource: 'providers'
        }),
        this.$store.dispatch('billers/LIST', {
          resource: 'billerconnections',
          query: { page_size: 1 }
        })
      ])
    })
    .catch((error) => {
      if (error.response) {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      }
    })
    .finally(() => {
      this.loaderRetrieve = false
    })

    this.trackEvent('Activation Checklist Viewed', { path: this.path })
  },
  methods: {
    navigate (routeName) {
      this.trackEvent('Activation Checklist Item Clicked', { path: this.path, route: routeName })
      this.$router.push({ name: routeName }).catch(() => {})
    }
  }
}
</script>
